import { toPredictionEventRef, type PredictionEventRef } from '@src/types/PredictionMarket';
import type {
  IPredictionOutcomeEvent,
  IPredictionOutcomeEventSeriesRecord,
  PredictionSecurityIndexes,
  Security,
} from '@talos/kyoko';

/** Everything a selector needs for one event, derived once per change to that event's outcomes. */
export type PredictionEventOutcomeIndex = {
  /** Outcomes for the event in security-index insertion order. */
  outcomes: Security[];
  /** Venues advertised by this event's outcomes. */
  markets: string[];
  /** Identity of the security-index inner map this was derived from. */
  indexedOutcomes: Map<string, Security> | undefined;
};

export type PredictionRefDataIndexes = {
  outcomeIndexByEventID: Map<string, PredictionEventOutcomeIndex>;
  /** EventID by EventSymbol, for card configs that persisted the symbol instead of the ID. */
  eventIDByEventSymbol: Map<string, string>;
  /** EventIDs that should appear in selectors for a given market. */
  eventIDsByMarket: Map<string, Set<string>>;
  /** SeriesIDs that still have at least one compatible event on a given market. */
  seriesIDsByMarket: Map<string, Set<string>>;
  supportedMarkets: string[];
};

export const EMPTY_PREDICTION_REF_DATA_INDEXES: PredictionRefDataIndexes = {
  outcomeIndexByEventID: new Map(),
  eventIDByEventSymbol: new Map(),
  eventIDsByMarket: new Map(),
  seriesIDsByMarket: new Map(),
  supportedMarkets: [],
};

/** Unique Markets advertised on outcome securities. No hardcoded venue fallback. */
export function collectSupportedMarkets(outcomes: readonly Security[]): string[] {
  const markets = new Set<string>();
  for (const outcome of outcomes) {
    for (const market of outcome.Markets ?? []) {
      markets.add(market);
    }
  }
  return [...markets];
}

function addToStringSetMap(map: Map<string, Set<string>>, key: string, value: string): void {
  const existing = map.get(key);
  if (existing) {
    existing.add(value);
    return;
  }
  map.set(key, new Set([value]));
}

export type PredictionSecurityIndexSource = Pick<
  PredictionSecurityIndexes,
  'predictionOutcomesByEventID' | 'predictionSupportedMarkets'
>;

/** Join event/series REST ref data to the security-derived prediction indexes. */
export function buildPredictionRefDataIndexes(
  predictionIndexes: PredictionSecurityIndexSource,
  events: readonly IPredictionOutcomeEvent[],
  previous?: PredictionRefDataIndexes
): PredictionRefDataIndexes {
  const { predictionOutcomesByEventID, predictionSupportedMarkets } = predictionIndexes;

  const eventIDByEventSymbol = new Map(events.map(event => [event.EventSymbol, event.EventID]));
  const outcomeIndexByEventID = new Map<string, PredictionEventOutcomeIndex>();
  const eventIDsByMarket = new Map<string, Set<string>>();
  const seriesIDsByMarket = new Map<string, Set<string>>();

  for (const event of events) {
    const eventID = event.EventID;
    const indexedOutcomes = predictionOutcomesByEventID.get(eventID);
    const cached = previous?.outcomeIndexByEventID.get(eventID);
    let outcomeIndex: PredictionEventOutcomeIndex | undefined;

    if (cached != null && cached.indexedOutcomes === indexedOutcomes) {
      outcomeIndex = cached;
    } else if (indexedOutcomes != null) {
      const outcomes = [...indexedOutcomes.values()];
      outcomeIndex = {
        outcomes,
        markets: collectSupportedMarkets(outcomes),
        indexedOutcomes,
      };
    }

    if (outcomeIndex != null) {
      outcomeIndexByEventID.set(eventID, outcomeIndex);
    }

    for (const market of outcomeIndex?.markets ?? []) {
      addToStringSetMap(eventIDsByMarket, market, eventID);
      if (event.SeriesID != null) {
        addToStringSetMap(seriesIDsByMarket, market, event.SeriesID);
      }
    }
  }

  return {
    outcomeIndexByEventID,
    eventIDByEventSymbol,
    eventIDsByMarket,
    seriesIDsByMarket,
    supportedMarkets: predictionSupportedMarkets,
  };
}

export function getIndexedOutcomesForPredictionEvent(
  event: PredictionEventRef | Pick<IPredictionOutcomeEvent, 'EventID' | 'EventSymbol' | 'EventName'>,
  indexes: PredictionRefDataIndexes
): Security[] {
  const eventRef = toPredictionEventRef(event);
  return indexes.outcomeIndexByEventID.get(eventRef.EventID)?.outcomes ?? [];
}

export function filterEventsByMarketIndex(
  events: readonly IPredictionOutcomeEvent[],
  market: string | undefined,
  indexes: PredictionRefDataIndexes,
  selectedEvent?: IPredictionOutcomeEvent
): IPredictionOutcomeEvent[] {
  if (!market) {
    return [...events];
  }
  const allowed = indexes.eventIDsByMarket.get(market);
  return events.filter(event => event.EventID === selectedEvent?.EventID || allowed?.has(event.EventID) === true);
}

function withSelectedSeries(
  seriesList: readonly IPredictionOutcomeEventSeriesRecord[],
  selectedSeries?: IPredictionOutcomeEventSeriesRecord
): IPredictionOutcomeEventSeriesRecord[] {
  if (!selectedSeries) {
    return [...seriesList];
  }
  if (seriesList.some(series => series.SeriesID === selectedSeries.SeriesID)) {
    return [...seriesList];
  }
  return [selectedSeries, ...seriesList];
}

export function filterSeriesByMarketIndex(
  seriesList: readonly IPredictionOutcomeEventSeriesRecord[],
  events: readonly IPredictionOutcomeEvent[],
  market: string | undefined,
  indexes: PredictionRefDataIndexes,
  selectedSeries?: IPredictionOutcomeEventSeriesRecord
): IPredictionOutcomeEventSeriesRecord[] {
  if (!market || events.length === 0) {
    return withSelectedSeries(seriesList, selectedSeries);
  }

  // Restrict to series that appear on the provided event list (e.g. create-mode selectableEvents).
  const seriesIDsFromEvents = new Set(events.filter(event => event.SeriesID != null).map(event => event.SeriesID));
  if (seriesIDsFromEvents.size === 0) {
    return withSelectedSeries(seriesList, selectedSeries);
  }

  const seriesIDsForMarket = indexes.seriesIDsByMarket.get(market);
  const filtered = seriesList.filter(series => {
    if (!seriesIDsFromEvents.has(series.SeriesID)) {
      return false;
    }
    return seriesIDsForMarket?.has(series.SeriesID) === true;
  });

  return withSelectedSeries(filtered, selectedSeries);
}
